import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ApiClientCreateRequest,
  ApiClientCreateResponse,
  ApiClientSecretRotationResponse,
  ApiClientView,
  ApiListWrapper,
  ApiPlatformSummary,
  ApiRateLimitPolicy,
  ApiResponseWrapper,
  ApiScopeDefinition,
  ApiUsageLogView,
  WebhookSecretRotationResponse,
  WebhookSubscriptionView
} from './developer-platform.model';

@Injectable({ providedIn: 'root' })
export class DeveloperPlatformService {
  private readonly baseUrl = `${environment.restApiUrl}developer-platform`;

  constructor(private readonly http: HttpClient) {}

  summary(): Observable<ApiResponseWrapper<ApiPlatformSummary>> {
    return this.http.get<ApiResponseWrapper<ApiPlatformSummary>>(`${this.baseUrl}/summary`);
  }

  scopes(): Observable<ApiResponseWrapper<ApiScopeDefinition[]>> {
    return this.http.get<ApiResponseWrapper<ApiScopeDefinition[]>>(`${this.baseUrl}/scopes`);
  }

  ratePlans(): Observable<ApiResponseWrapper<ApiRateLimitPolicy[]>> {
    return this.http.get<ApiResponseWrapper<ApiRateLimitPolicy[]>>(`${this.baseUrl}/rate-plans`);
  }

  clients(): Observable<ApiResponseWrapper<ApiClientView[]>> {
    return this.http.get<ApiResponseWrapper<ApiClientView[]>>(`${this.baseUrl}/clients`);
  }

  createClient(request: ApiClientCreateRequest): Observable<ApiResponseWrapper<ApiClientCreateResponse>> {
    return this.http.post<ApiResponseWrapper<ApiClientCreateResponse>>(`${this.baseUrl}/clients`, request);
  }

  updateClient(client: ApiClientView): Observable<ApiResponseWrapper<ApiClientView>> {
    return this.http.put<ApiResponseWrapper<ApiClientView>>(`${this.baseUrl}/clients/${client.apiClientId}`, client);
  }

  rotateSecret(client: ApiClientView): Observable<ApiResponseWrapper<ApiClientSecretRotationResponse>> {
    return this.http.post<ApiResponseWrapper<ApiClientSecretRotationResponse>>(`${this.baseUrl}/clients/${client.apiClientId}/rotate-secret`, {});
  }

  revokeClient(client: ApiClientView): Observable<ApiResponseWrapper<ApiClientView>> {
    return this.http.post<ApiResponseWrapper<ApiClientView>>(`${this.baseUrl}/clients/${client.apiClientId}/revoke`, {});
  }

  usage(page = 0, pageSize = 50): Observable<ApiListWrapper<ApiUsageLogView>> {
    return this.http.get<ApiListWrapper<ApiUsageLogView>>(`${this.baseUrl}/usage`, {
      params: { page, pageSize }
    });
  }

  webhooks(): Observable<ApiResponseWrapper<WebhookSubscriptionView[]>> {
    return this.http.get<ApiResponseWrapper<WebhookSubscriptionView[]>>(`${this.baseUrl}/webhooks`);
  }

  createWebhook(request: Partial<WebhookSubscriptionView>): Observable<ApiResponseWrapper<WebhookSecretRotationResponse>> {
    return this.http.post<ApiResponseWrapper<WebhookSecretRotationResponse>>(`${this.baseUrl}/webhooks`, request);
  }

  updateWebhook(webhook: WebhookSubscriptionView): Observable<ApiResponseWrapper<WebhookSubscriptionView>> {
    return this.http.put<ApiResponseWrapper<WebhookSubscriptionView>>(`${this.baseUrl}/webhooks/${webhook.webhookSubscriptionId}`, webhook);
  }

  rotateWebhookSecret(webhook: WebhookSubscriptionView): Observable<ApiResponseWrapper<WebhookSecretRotationResponse>> {
    return this.http.post<ApiResponseWrapper<WebhookSecretRotationResponse>>(`${this.baseUrl}/webhooks/${webhook.webhookSubscriptionId}/rotate-secret`, {});
  }

  deleteWebhook(webhook: WebhookSubscriptionView): Observable<ApiResponseWrapper<void>> {
    return this.http.delete<ApiResponseWrapper<void>>(`${this.baseUrl}/webhooks/${webhook.webhookSubscriptionId}`);
  }
}
